import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import HowItWorksSection from './components/HowItWorksSection';
import FeaturesSection from './components/FeaturesSection';
import MissionSection from './components/MissionSection';
import PricingSection from './components/PricingSection';
import TestimonialsSection from './components/TestimonialsSection';
import FAQSection from './components/FAQSection';
import CTASection from './components/CTASection';

const Homepage = () => {
  useEffect(() => {
    // Handle anchor links from navigation
    const handleHashChange = () => {
      const hash = window.location?.hash;
      if (hash) {
        const element = document.getElementById(hash?.substring(1));
        if (element) {
          setTimeout(() => {
            element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        }
      }
    };

    // Handle initial hash on page load
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Workfolio - Anonymous Skills-First Recruitment Platform | Norway</title>
        <meta 
          name="description" 
          content="Norway's first anonymous recruitment platform. Job seekers create free profiles, employers discover talent without bias. Pay only 149 NOK to unlock contact details. Fair, fast, efficient hiring." 
        />
        <meta name="keywords" content="anonymous recruitment, skills-first hiring, Norway jobs, bias-free recruitment, diverse hiring, job search platform" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Workfolio - Anonymous Skills-First Recruitment Platform" />
        <meta property="og:description" content="Join Norway's first anonymous recruitment platform. Free for job seekers, pay-per-unlock for employers. Eliminate hiring bias and find the right talent." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://workfolio.no" />
        <meta property="og:image" content="https://workfolio.no/og-image.jpg" />
        <meta property="og:locale" content="nb_NO" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Workfolio - Anonymous Skills-First Recruitment" />
        <meta name="twitter:description" content="Norway's first anonymous recruitment platform. Free profiles for job seekers, pay-per-unlock for employers." />
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Workfolio",
            "description": "Anonymous skills-first recruitment platform connecting job seekers with employers in Norway",
            "url": "https://workfolio.no",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://workfolio.no/search?q={search_term_string}",
              "query-input": "required name=search_term_string"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Workfolio AS",
              "address": {
                "@type": "PostalAddress",
                "addressCountry": "NO",
                "addressLocality": "Oslo"
              }
            }
          })}
        </script>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main>
          <HeroSection />
          <HowItWorksSection />
          <FeaturesSection />
          <MissionSection />
          <PricingSection />
          <TestimonialsSection />
          <FAQSection />
          <CTASection />
        </main>

        {/* Footer */}
        <footer className="bg-secondary text-secondary-foreground py-16">
          <div className="container mx-auto px-6 lg:px-8">
            <div className="grid md:grid-cols-4 gap-8 mb-12">
              {/* Company Info */}
              <div className="md:col-span-2">
                <div className="flex items-center space-x-2 mb-6">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                      <line x1="8" y1="21" x2="16" y2="21"/>
                      <line x1="12" y1="17" x2="12" y2="21"/>
                    </svg>
                  </div>
                  <span className="text-xl font-semibold">Workfolio</span>
                </div>
                
                <p className="text-secondary-foreground/80 mb-6 max-w-md leading-relaxed">
                  Norway's first anonymous recruitment platform, connecting talent with opportunity 
                  based on skills and potential, not bias.
                </p>
                
                <div className="flex space-x-4">
                  <a href="#" className="text-secondary-foreground/60 hover:text-secondary-foreground transition-colors">
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                    </svg>
                  </a>
                  <a href="#" className="text-secondary-foreground/60 hover:text-secondary-foreground transition-colors">
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>
                </div>
              </div>
              
              {/* Quick Links */}
              <div>
                <h3 className="font-semibold text-secondary-foreground mb-4">Platform</h3>
                <ul className="space-y-3">
                  <li><a href="/job-seeker-registration" className="text-secondary-foreground/80 hover:text-secondary-foreground transition-colors">For Job Seekers</a></li>
                  <li><a href="/employer-registration" className="text-secondary-foreground/80 hover:text-secondary-foreground transition-colors">For Employers</a></li>
                  <li><a href="#how-it-works" className="text-secondary-foreground/80 hover:text-secondary-foreground transition-colors">How It Works</a></li>
                  <li><a href="#pricing" className="text-secondary-foreground/80 hover:text-secondary-foreground transition-colors">Pricing</a></li>
                </ul>
              </div>
              
              {/* Support */}
              <div>
                <h3 className="font-semibold text-secondary-foreground mb-4">Support</h3>
                <ul className="space-y-3">
                  <li><a href="/contact" className="text-secondary-foreground/80 hover:text-secondary-foreground transition-colors">Contact Us</a></li>
                  <li><a href="#faq" className="text-secondary-foreground/80 hover:text-secondary-foreground transition-colors">FAQ</a></li>
                  <li><a href="/privacy" className="text-secondary-foreground/80 hover:text-secondary-foreground transition-colors">Privacy Policy</a></li>
                  <li><a href="/terms" className="text-secondary-foreground/80 hover:text-secondary-foreground transition-colors">Terms of Service</a></li>
                </ul>
              </div>
            </div>
            
            {/* Bottom Bar */}
            <div className="border-t border-secondary-foreground/20 pt-8 flex flex-col md:flex-row justify-between items-center">
              <div className="text-secondary-foreground/60 text-sm mb-4 md:mb-0">
                © {new Date()?.getFullYear()} Workfolio AS. All rights reserved.
              </div>
              
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center space-x-2 text-secondary-foreground/80">
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  <span>GDPR Compliant</span>
                </div>
                
                <div className="flex items-center space-x-2 text-secondary-foreground/80">
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7C13.4,7 14.8,8.6 14.8,10V11C15.4,11 16,11.4 16,12V16C16,16.6 15.6,17 15,17H9C8.4,17 8,16.6 8,16V12C8,11.4 8.4,11 9,11V10C9,8.6 10.6,7 12,7M12,8.2C11.2,8.2 10.2,9.2 10.2,10V11H13.8V10C13.8,9.2 12.8,8.2 12,8.2Z"/>
                  </svg>
                  <span>Made in Norway</span>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Homepage;